import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { differenceInDays, parseISO } from 'date-fns';
import { getHojeBR, toDataBR } from '../utils/dataBR';

export function useDetox(userId) {
  const [enrollment, setEnrollment] = useState(null);   // detox_enrollments row
  const [checkins, setCheckins] = useState([]);          // detox_checkins rows
  const [communityCount, setCommunityCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Which day of the challenge is today? (1-7, or null if not started / finished)
  const currentDay = enrollment
    ? Math.min(7, differenceInDays(parseISO(getHojeBR()), parseISO(toDataBR(enrollment.started_at))) + 1)
    : null;

  // Has the user checked in today?
  const todayCheckin = checkins.find(c => c.day_number === currentDay) || null;

  const fetchAll = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);

    const [{ data: enrollData }, { data: checkinData }, { data: statsData }] = await Promise.all([
      supabase.from('detox_enrollments').select('*').eq('user_id', userId).single(),
      supabase.from('detox_checkins').select('*').eq('user_id', userId).order('day_number'),
      supabase.from('detox_community_stats').select('active_count').single(),
    ]);

    setEnrollment(enrollData || null);
    setCheckins(checkinData || []);
    setCommunityCount(statsData?.active_count ?? 0);
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Enroll in the challenge
  const enroll = async (initialScreenTime) => {
    const { data, error } = await supabase
      .from('detox_enrollments')
      .insert([{ user_id: userId, initial_screen_time: initialScreenTime }])
      .select()
      .single();
    if (!error) setEnrollment(data);
    return { error };
  };

  // Daily check-in
  const checkInDay = async ({ stayedOff, difficulty, cravingScore, reflection }) => {
    if (!currentDay) return { error: 'Desafio não iniciado' };

    const payload = {
      user_id: userId,
      day_number: currentDay,
      stayed_off: stayedOff,
      difficulty,
      craving_score: cravingScore,
      reflection,
    };

    // Upsert in case they're updating the same day
    const { data, error } = await supabase
      .from('detox_checkins')
      .upsert(payload, { onConflict: 'user_id,day_number' })
      .select()
      .single();

    if (!error) {
      setCheckins(prev => {
        const filtered = prev.filter(c => c.day_number !== currentDay);
        return [...filtered, data].sort((a, b) => a.day_number - b.day_number);
      });
    }
    return { error };
  };

  // Complete the challenge on day 7
  const completeChallenge = async ({ finalScreenTime, finalMood }) => {
    const { error } = await supabase
      .from('detox_enrollments')
      .update({
        completed: true,
        completed_at: new Date().toISOString(),
        final_screen_time: finalScreenTime,
        final_mood: finalMood,
      })
      .eq('user_id', userId);

    if (!error) {
      setEnrollment(prev => ({
        ...prev,
        completed: true,
        final_screen_time: finalScreenTime,
        final_mood: finalMood,
      }));
    }
    return { error };
  };

  return {
    enrollment,
    checkins,
    communityCount,
    currentDay,
    todayCheckin,
    loading,
    enroll,
    checkInDay,
    completeChallenge,
    refresh: fetchAll,
  };
}
